import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, MessageCircle } from "lucide-react";
import { waLink } from "@/lib/contact";

const wrap = {
  hidden: {},
  show: { transition: { staggerChildren: 0.16, delayChildren: 0.35 } },
};

const lineReveal = {
  hidden: { y: "115%" },
  show: { y: "0%", transition: { duration: 1.05, ease: [0.16, 1, 0.3, 1] } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

const STATS = [
  { value: "120+", label: "Sites Equipped" },
  { value: "6", label: "Product Lines" },
  { value: "48hr", label: "Bulk Dispatch" },
];

export const Hero = () => {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 220]);
  const bgScale = useTransform(scrollY, [0, 900], [1, 1.12]);
  const fade = useTransform(scrollY, [0, 500], [1, 0]);

  return (
    <section id="top" className="relative min-h-screen flex flex-col justify-end overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img
          src="https://images.unsplash.com/photo-1669206687431-057031e59053?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800"
          alt="Road safety infrastructure at night"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0B0F17]/72" />
        <div className="absolute inset-0 bg-blueprint" />
        <div className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-[#0B0F17] to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto w-full px-6 lg:px-10 pt-36 pb-16">
        <motion.div variants={wrap} initial="hidden" animate="show">
          <motion.p
            variants={fadeUp}
            className="font-mono2 text-xs tracking-[0.35em] uppercase text-amber-400 mb-8 flex items-center gap-3"
            data-testid="hero-eyebrow"
          >
            <span className="w-8 h-px bg-amber-500 inline-block" />
            Manufacturer & Supplier · Road Safety Signage
          </motion.p>

          <h1 className="font-display font-bold uppercase leading-[0.92] tracking-tight text-5xl sm:text-6xl lg:text-8xl text-white" data-testid="hero-heading">
            <span className="block overflow-hidden pb-1">
              <motion.span variants={lineReveal} className="block">Safety,</motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span variants={lineReveal} className="block text-stroke-amber">Engineered</motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span variants={lineReveal} className="block">
                To Be <span className="text-amber-500">Seen.</span>
              </motion.span>
            </span>
          </h1>

          <motion.p
            variants={fadeUp}
            className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-zinc-300"
            data-testid="hero-subtext"
          >
            Rorix builds the hardware that keeps Indian roads disciplined — boom barriers,
            speed breakers, sign boards and reflective systems, manufactured in-house and
            dispatched in bulk to contractors, townships and infrastructure projects.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#contact"
              data-testid="hero-inquiry-button"
              className="clip-notch group bg-amber-500 text-[#0B0F17] font-display font-bold tracking-widest uppercase px-7 py-4 flex items-center gap-2 hover:bg-amber-400 transition-colors duration-300"
            >
              Request Bulk Quote
              <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              className="group border border-zinc-600 text-white font-display font-bold tracking-widest uppercase px-7 py-4 flex items-center gap-2 hover:border-emerald-400 hover:text-emerald-400 transition-colors duration-300"
            >
              <MessageCircle className="w-5 h-5" />
              WhatsApp Bhargav
            </a>
          </motion.div>

          <motion.div variants={fadeUp} className="mt-14 grid grid-cols-3 max-w-lg border-t border-zinc-700/60 pt-6 gap-6" data-testid="hero-stats">
            {STATS.map((s) => (
              <div key={s.label}>
                <div className="font-display text-3xl sm:text-4xl font-bold text-amber-500">{s.value}</div>
                <div className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-zinc-500 mt-1">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1 }}
        className="relative z-10 flex justify-center pb-6"
      >
        <ArrowDown className="w-5 h-5 text-amber-500 animate-bounce" />
      </motion.div>
    </section>
  );
};
